import {
  Leaf,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  GitCompare,
  ArrowUpFromLine,
  Unplug,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { GroupStatsSummary } from '@/types/reports';
import { cn } from '@/lib/utils';

interface StatsSummaryCardProps {
  stats: GroupStatsSummary | null;
  title?: string;
}

function StatRow({
  label,
  value,
  icon,
  highlight,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  highlight?: 'positive' | 'negative' | 'neutral';
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-muted-foreground flex items-center gap-1.5">
        {icon}
        {label}
      </span>
      <span className={cn(
        "text-sm font-medium tabular-nums",
        highlight === 'positive' && "text-[#8FB560]",
        highlight === 'negative' && "text-amber-500"
      )}>
        {value}
      </span>
    </div>
  );
}

function DeltaIndicator({ value, label }: { value: number; label: string }) {
  const isPositive = value > 0;
  const isNeutral = value === 0;

  return (
    <div className="flex items-center gap-1 text-xs">
      <span className="text-muted-foreground">{label}:</span>
      <span className={cn(
        "font-medium tabular-nums inline-flex items-center gap-0.5",
        isNeutral ? "text-muted-foreground" : isPositive ? "text-[#8FB560]" : "text-amber-500"
      )}>
        {isPositive ? <TrendingUp className="h-3 w-3" /> : isNeutral ? <Minus className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
        {isPositive ? '+' : ''}{value.toFixed(1)}%
      </span>
    </div>
  );
}

export function StatsSummaryCard({
  stats,
  title = 'Sustainability Summary',
}: StatsSummaryCardProps) {
  // Empty state - maintains same layout structure to prevent shift
  if (!stats) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="pb-2 flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <Leaf className="h-4 w-4 text-[#8FB560] opacity-50" />
              {title}
            </CardTitle>
            <span className="text-xs text-muted-foreground/50 tabular-nums flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              --
            </span>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col justify-between">
          {/* Environmental Impact placeholder */}
          <div>
            <div className="space-y-0 divide-y divide-border/50">
              {['CO₂ Reduced', 'Diesel Reduced', 'Trees Equivalent'].map((label) => (
                <div key={label} className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground/50">{label}</span>
                  <span className="text-sm font-medium text-muted-foreground/30">--</span>
                </div>
              ))}
            </div>
          </div>
          {/* Off-Grid placeholder */}
          <div className="pt-4 mt-4 border-t border-border">
            <span className="text-xs font-medium text-muted-foreground/50 uppercase tracking-wider flex items-center gap-1.5">
              <Unplug className="h-3.5 w-3.5 text-[#8FB560] opacity-50" />
              Off-Grid
            </span>
            <div className="mt-2 space-y-0 divide-y divide-border/50">
              {['Off-Grid', 'Gen Hours Reduced'].map((label) => (
                <div key={label} className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground/50">{label}</span>
                  <span className="text-sm font-medium text-muted-foreground/30">--</span>
                </div>
              ))}
            </div>
          </div>
          {/* Grid Export placeholder */}
          <div className="pt-4 mt-4 border-t border-border">
            <span className="text-xs font-medium text-muted-foreground/50 uppercase tracking-wider flex items-center gap-1.5">
              <ArrowUpFromLine className="h-3.5 w-3.5 text-[#8FB560] opacity-50" />
              Grid Export
            </span>
            <div className="mt-2 space-y-0 divide-y divide-border/50">
              {['Energy Exported', 'Grid Revenue'].map((label) => (
                <div key={label} className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground/50">{label}</span>
                  <span className="text-sm font-medium text-muted-foreground/30">--</span>
                </div>
              ))}
            </div>
          </div>
          {/* Period Comparison placeholder */}
          <div className="pt-4 mt-4 border-t border-border">
            <span className="text-xs font-medium text-muted-foreground/50 uppercase tracking-wider flex items-center gap-1.5">
              <GitCompare className="h-3.5 w-3.5 text-[#8FB560] opacity-50" />
              vs Prior Period
            </span>
            <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1">
              {['Solar', 'Off-Grid', 'CO₂'].map((label) => (
                <div key={label} className="flex items-center gap-1 text-xs">
                  <span className="text-muted-foreground/50">{label}:</span>
                  <span className="font-medium text-muted-foreground/30">--</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <Leaf className="h-4 w-4 text-[#8FB560]" />
            {title}
          </CardTitle>
          {stats.daysInPeriod > 0 && (
            <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {stats.daysInPeriod}d
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col justify-between">
        {/* Environmental Impact - no sub-header, starts directly */}
        <div>
          <div className="space-y-0 divide-y divide-border/50">
            <StatRow
              label="CO₂ Reduced"
              value={`${Math.round(stats.totalCO2).toLocaleString()} kg`}
              highlight="positive"
            />
            <StatRow
              label="Diesel Reduced"
              value={`${stats.dieselGallonsReduced.toLocaleString()} gal`}
              highlight="positive"
            />
            <StatRow
              label="Trees Equivalent"
              value={`≈ ${stats.treesEquivalent.toLocaleString()} trees`}
              highlight="positive"
            />
          </div>
        </div>

        {/* Off-Grid */}
        <div className="pt-4 mt-4 border-t border-border">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
            <Unplug className="h-3.5 w-3.5 text-[#8FB560]" />
            Off-Grid
          </span>
          <div className="mt-2 space-y-0 divide-y divide-border/50">
            <StatRow
              label="Off-Grid"
              value={`${stats.avgOffGrid}%`}
              highlight={stats.avgOffGrid >= 70 ? 'positive' : stats.avgOffGrid < 50 ? 'negative' : 'neutral'}
            />
            <StatRow
              label="Gen Hours Reduced"
              value={stats.genHoursReduced == null ? 'N/A' : `${stats.genHoursReduced.toLocaleString()} hrs`}
              highlight={stats.genHoursReduced == null ? 'neutral' : 'positive'}
            />
          </div>
        </div>

        {/* Grid Export */}
        <div className="pt-4 mt-4 border-t border-border">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
            <ArrowUpFromLine className="h-3.5 w-3.5 text-[#8FB560]" />
            Grid Export
          </span>
          <div className="mt-2 space-y-0 divide-y divide-border/50">
            <StatRow
              label="Energy Exported"
              value={stats.totalGridExportKwh !== undefined && stats.totalGridExportKwh >= 1_000_000
                ? `${(stats.totalGridExportKwh / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GWh`
                : `${Math.round(stats.totalGridExportKwh ?? 0).toLocaleString()} kWh`}
              highlight={stats.totalGridExportKwh && stats.totalGridExportKwh > 0 ? 'positive' : 'neutral'}
            />
            <StatRow
              label="Grid Revenue"
              value={stats.gridSellRevenue !== undefined && stats.gridSellRevenue > 0
                ? `$${Math.round(stats.gridSellRevenue).toLocaleString()}`
                : '—'}
              highlight={stats.gridSellRevenue && stats.gridSellRevenue > 0 ? 'positive' : 'neutral'}
            />
          </div>
        </div>

        {/* Period Comparison — hidden until real per-metric prior-period
            deltas exist (the old chips repeated one within-range trend). */}
        {(stats.priorPeriodComparison.solarKwhDelta != null
          || stats.priorPeriodComparison.offGridDelta != null
          || stats.priorPeriodComparison.co2Delta != null) && (
          <div className="pt-4 mt-4 border-t border-border">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <GitCompare className="h-3.5 w-3.5 text-[#8FB560]" />
              vs Prior Period
            </span>
            <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1">
              {stats.priorPeriodComparison.solarKwhDelta != null && (
                <DeltaIndicator label="Solar" value={stats.priorPeriodComparison.solarKwhDelta} />
              )}
              {stats.priorPeriodComparison.offGridDelta != null && (
                <DeltaIndicator label="Off-Grid" value={stats.priorPeriodComparison.offGridDelta} />
              )}
              {stats.priorPeriodComparison.co2Delta != null && (
                <DeltaIndicator label="CO₂" value={stats.priorPeriodComparison.co2Delta} />
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
